import { Grid, Stack, Typography } from "@mui/material";
import { FormSection } from "Components/Shared/FormSection";
import { GridItem } from "Components/Shared/GridItem";
import {
  ProgramCode,
  type ProgramParticipationDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const FORM_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.User.Form;

// Each program's texts are keyed by the API enum values (states, sources), so the dictionary is
// read directly — no extra map from enum to translation key.
const PROGRAM_TO_RESOURCES = {
  [ProgramCode.Stardust]: FORM_RESOURCES.Stardust,
  [ProgramCode.ThanksToYou]: FORM_RESOURCES.ThanksToYou,
} as const;

type Props = {
  /** Which program it is comes with the participation itself. Null for accounts without a customer side (no program at all) — the section is then hidden. */
  participation: ProgramParticipationDto | null | undefined;
  /** The read-only detail spaces its sections itself; the admin form relies on the section's own bottom margin. */
  noMargins?: boolean;
};

/**
 * A program participation of the person behind the account — Stardust (ZS-1624) or Díky Tobě
 * (ZS-1623): in, out, left for good or never asked, and since when / where the last answer came
 * from — what support needs for "why don't I earn". Read-only on purpose: the participation is the
 * person's own decision, never the back office's.
 */
export const ProgramParticipationSection = ({
  participation,
  noMargins,
}: Props) => {
  const { t } = useResource();

  if (!participation) {
    return null;
  }

  const { program, state, changedAt, source } = participation;
  const resources = PROGRAM_TO_RESOURCES[program];
  const hasAnswer = !!changedAt && !!source;

  return (
    <FormSection legend={t(resources.Legend)} noMargins={noMargins}>
      <Grid container spacing={2}>
        <GridItem
          columns={2}
          label={t(FORM_RESOURCES.Status)}
          value={
            <Stack>
              <span>{t(resources[state])}</span>
              {hasAnswer && (
                <Typography variant="body2" color="text.secondary">
                  {t(resources.ChangedAt, {
                    date: formatDate(changedAt, true),
                    source: t(resources.Source[source]),
                  })}
                </Typography>
              )}
            </Stack>
          }
        />
      </Grid>
    </FormSection>
  );
};
